import { z } from "zod";

/** 1 credit = US$0.01. All prices below are PLACEHOLDERS until the cost model exists (BILL-9). */
export const CREDIT_VALUE_USD_CENTS = 1;

export const PackSchema = z.object({
  id: z.string(),
  name: z.string(),
  kitIds: z.array(z.string()).min(1),
  maxRecords: z.number().int().positive(),
  priceCents: z.number().int().positive(),
});
export type Pack = z.infer<typeof PackSchema>;

export const CreditBundleSchema = z.object({
  id: z.string(),
  credits: z.number().int().positive(),
  priceCents: z.number().int().positive(),
});
export type CreditBundle = z.infer<typeof CreditBundleSchema>;

export const PriceBookSchema = z.object({
  placeholder: z.boolean().default(true),
  /** Credits per 1,000 records. */
  rates: z.object({
    import_per_1k: z.number().nonnegative(),
    dryrun_per_1k: z.number().nonnegative(),
    export_per_1k: z.number().nonnegative(),
    order_export_per_1k: z.number().nonnegative(),
    revert_per_1k: z.number().nonnegative(),
    storage_per_gb_month: z.number().nonnegative(),
  }),
  freePreviewRecords: z.number().int().nonnegative(),
  minimumPurchaseCents: z.number().int().nonnegative(),
  defaultWholesaleDiscountPct: z.number().min(0).max(90),
  bundles: z.array(CreditBundleSchema),
  packs: z.array(PackSchema),
});
export type PriceBook = z.infer<typeof PriceBookSchema>;

export const DEFAULT_PRICE_BOOK: PriceBook = {
  placeholder: true,
  rates: {
    import_per_1k: 20, // $0.20 per 1k records read
    dryrun_per_1k: 10, // full dry runs beyond the free preview
    export_per_1k: 100, // $1.00 per 1k records written
    order_export_per_1k: 300, // orders are slower and riskier
    revert_per_1k: 0, // reverts are free: trust is the product
    storage_per_gb_month: 50, // $0.50 per GB-month
  },
  freePreviewRecords: 1000,
  minimumPurchaseCents: 2000,
  defaultWholesaleDiscountPct: 25,
  bundles: [
    { id: "credits-2k", credits: 2_000, priceCents: 2_000 },
    { id: "credits-10k", credits: 10_000, priceCents: 9_500 },
    { id: "credits-50k", credits: 50_000, priceCents: 45_000 },
  ],
  packs: [
    { id: "klaviyo-50k", name: "Klaviyo migration up to 50k profiles", kitIds: ["mailchimp-to-klaviyo", "sfmc-to-klaviyo", "omnisend-to-klaviyo", "attentive-to-klaviyo", "csv-to-klaviyo"], maxRecords: 50_000, priceCents: 29_900 },
    { id: "klaviyo-250k", name: "Klaviyo migration up to 250k profiles", kitIds: ["mailchimp-to-klaviyo", "sfmc-to-klaviyo", "omnisend-to-klaviyo", "attentive-to-klaviyo", "csv-to-klaviyo"], maxRecords: 250_000, priceCents: 79_900 },
    { id: "shopify-10k", name: "Shopify replatform up to 10k products", kitIds: ["bigcommerce-to-shopify", "woocommerce-to-shopify", "magento-to-shopify", "csv-to-shopify"], maxRecords: 10_000, priceCents: 49_900 },
  ],
};

export type Meter = "import" | "dryrun" | "export" | "order_export" | "revert";

export function costFor(book: PriceBook, meter: Meter, records: number): number {
  const rate = book.rates[`${meter}_per_1k` as const];
  return Math.ceil((Math.max(0, records) * rate) / 1000);
}

/** Free preview: the first `freePreviewRecords` of a dry run cost nothing (BILL-6). */
export function dryRunCost(book: PriceBook, records: number): number {
  return costFor(book, "dryrun", Math.max(0, records - book.freePreviewRecords));
}

export function storageCostForMonth(book: PriceBook, bytes: number): number {
  return Math.ceil((bytes / 1024 ** 3) * book.rates.storage_per_gb_month);
}

/** Daily accrual so storage is charged smoothly (BILL-7). */
export function storageCostForDay(book: PriceBook, bytes: number, daysInMonth = 30): number {
  return Math.ceil(((bytes / 1024 ** 3) * book.rates.storage_per_gb_month) / daysInMonth);
}

export function wholesalePriceCents(listCents: number, discountPct: number): number {
  return Math.round(listCents * (1 - discountPct / 100));
}

export function findPackForRun(packs: Pack[], kitId: string, records: number): Pack | undefined {
  return packs
    .filter((p) => p.kitIds.includes(kitId) && p.maxRecords >= records)
    .sort((a, b) => a.maxRecords - b.maxRecords)[0];
}
